import { Box, Skeleton, Typography } from '@mui/material';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Image3D } from '~/domain/patient/image3d';
import { formatSeriesDate } from '~/utils/date';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';

type ImageDataPreviewProps = {
    image?: Image3D;
    isLoading?: boolean;
};

export default function ImageDataPreview({
    image,
    isLoading,
}: ImageDataPreviewProps) {
    return isLoading ? (
        <SkeletonLoader />
    ) : image ? (
        <ImageData image={image} />
    ) : (
        <Typography variant="body2">No image selected</Typography>
    );
}

const ImageData = ({ image }: { image: Image3D }) => {
    const { modality, length, series, imageType } = transform3DImageData(image);
    const hasSeriesDescription = series.description !== '';
    return (
        <Box>
            <Box
                display="flex"
                gap={1}
                alignItems="center"
                justifyContent="space-between"
            >
                <Box display="flex" gap={0.5} alignItems="end">
                    <Typography>{modality}</Typography>
                    <Typography color="primary" fontSize={12} lineHeight={1.8}>
                        ({length})
                    </Typography>
                </Box>

                <SensitiveInfoTypography>
                    <Typography component="span" fontSize={12} variant="body2">
                        {formatSeriesDate(series?.date)}
                    </Typography>
                </SensitiveInfoTypography>
            </Box>
            <Typography
                fontSize={12}
                sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {imageType.join(', ')}
            </Typography>
            <SensitiveInfoTypography>
                <Typography
                    component="span"
                    fontSize={12}
                    fontStyle={hasSeriesDescription ? 'normal' : 'italic'}
                    variant="body2"
                    sx={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {hasSeriesDescription
                        ? series.description
                        : 'No series description'}
                </Typography>
            </SensitiveInfoTypography>
        </Box>
    );
};

const SkeletonLoader = () => (
    <Box>
        <Skeleton variant="text" width="20%" height={24} />
        <Skeleton variant="text" width="60%" height={18} />
        <Skeleton variant="text" width="80%" height={18} />
    </Box>
);
